import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { InvoiceItem } from '../services/generateInvoicePdf';
import type { Json } from '@/integrations/supabase/types';

export interface Invoice {
  id: string;
  owner_id: string;
  session_id: string | null;
  invoice_number: string;
  client_id: string | null;
  client_name: string;
  client_email: string;
  client_address: Record<string, string>;
  items: InvoiceItem[];
  total_ht: number;
  total_vat: number;
  total_ttc: number;
  deposit_amount: number;
  pdf_url: string | null;
  status: string;
  issue_date: string;
  due_date: string | null;
  payment_method: string;
  notes: string;
  payment_id: string | null;
  credit_note_for: string | null;
  cancelled_by: string | null;
  created_at: string;
  updated_at: string;
}

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'cancelled' | 'credit_note';

export const INVOICE_STATUSES: { value: InvoiceStatus; label: Record<'fr' | 'en' | 'es', string> }[] = [
  { value: 'draft',       label: { fr: 'Brouillon',  en: 'Draft',        es: 'Borrador'          } },
  { value: 'sent',        label: { fr: 'Envoyée',    en: 'Sent',         es: 'Enviada'           } },
  { value: 'paid',        label: { fr: 'Payée',      en: 'Paid',         es: 'Pagada'            } },
  { value: 'cancelled',   label: { fr: 'Annulée',    en: 'Cancelled',    es: 'Cancelada'         } },
  { value: 'credit_note', label: { fr: 'Avoir',      en: 'Credit Note',  es: 'Nota de crédito'   } },
];

export function useInvoices() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['invoices', user?.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('invoices')
        .select('*')
        .eq('owner_id', user!.id)
        .eq('type', 'invoice')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []).map((d) => ({
        ...d,
        items: (d.items as unknown as InvoiceItem[]) || [],
        client_address: (d.client_address as unknown as Record<string, string>) || {},
        credit_note_for: d.credit_note_for ?? null,
        cancelled_by:    d.cancelled_by    ?? null,
      })) as unknown as Invoice[];
    },
    enabled: !!user,
  });
}

export function useNextInvoiceNumber() {
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (): Promise<string> => {
      if (!user) throw new Error('Not authenticated');
      const year = new Date().getFullYear();

      const { data: existing } = await (supabase as any)
        .from('invoice_counters')
        .select('*')
        .eq('owner_id', user.id)
        .eq('doc_type', 'invoice')
        .maybeSingle();

      let nextNum: number;

      if (!existing) {
        nextNum = 1;
        await (supabase as any).from('invoice_counters').insert({
          owner_id: user.id,
          doc_type: 'invoice',
          current_year: year,
          current_number: 1,
        });
      } else if (existing.current_year !== year) {
        nextNum = 1;
        await (supabase as any)
          .from('invoice_counters')
          .update({ current_year: year, current_number: 1 })
          .eq('owner_id', user.id)
          .eq('doc_type', 'invoice');
      } else {
        nextNum = existing.current_number + 1;
        await (supabase as any)
          .from('invoice_counters')
          .update({ current_number: nextNum })
          .eq('owner_id', user.id)
          .eq('doc_type', 'invoice');
      }

      return `FAC-${year}-${String(nextNum).padStart(4, '0')}`;
    },
  });
}

export function useSaveInvoice() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (
      invoice: Partial<Invoice> & {
        invoice_number: string;
        client_name: string;
        items: InvoiceItem[];
      },
    ) => {
      const row = {
        invoice_number: invoice.invoice_number,
        amount: invoice.total_ttc || 0,
        session_id: invoice.session_id || null,
        client_id: invoice.client_id || undefined,
        client_name: invoice.client_name,
        client_email: invoice.client_email || '',
        client_address: (invoice.client_address || {}) as unknown as Json,
        items: invoice.items as unknown as Json,
        total_ht: invoice.total_ht || 0,
        total_vat: invoice.total_vat || 0,
        total_ttc: invoice.total_ttc || 0,
        deposit_amount: invoice.deposit_amount || 0,
        status: invoice.status || 'draft',
        issue_date: invoice.issue_date || new Date().toISOString().split('T')[0],
        due_date: invoice.due_date || null,
        payment_method: invoice.payment_method || null,
        notes: invoice.notes || '',
        payment_id: invoice.payment_id || null,
      };

      if (invoice.id) {
        const { error } = await (supabase as any)
          .from('invoices')
          .update(row)
          .eq('id', invoice.id);
        if (error) throw error;
      } else {
        const { error } = await (supabase as any)
          .from('invoices')
          .insert({ ...row, owner_id: user!.id });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  });
}

export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data: inv } = await (supabase as any)
        .from('invoices').select('status').eq('id', id).single();
      if (inv && inv.status !== 'draft') {
        throw new Error("Une facture émise ne peut pas être supprimée. Émettez un avoir pour l'annuler.");
      }
      const { error } = await (supabase as any).from('invoices').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  });
}

export function useCreateCreditNote() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceId: string): Promise<string> => {
      if (!user) throw new Error('Not authenticated');
      const { data: orig, error: fetchErr } = await (supabase as any)
        .from('invoices').select('*').eq('id', invoiceId).single();
      if (fetchErr || !orig) throw new Error('Facture introuvable');

      const { data: cn, error } = await (supabase as any)
        .from('invoices')
        .insert({
          owner_id:        user.id,
          type:            'invoice',
          invoice_number:  `AV-${orig.invoice_number}`,
          client_id:       orig.client_id,
          client_name:     orig.client_name,
          client_email:    orig.client_email,
          client_address:  orig.client_address,
          session_id:      orig.session_id,
          items:           (orig.items || []).map((item: any) => ({
            ...item, unit_price: -(Math.abs(item.unit_price ?? 0)),
          })),
          total_ht:        -(Math.abs(orig.total_ht  || 0)),
          total_vat:       -(Math.abs(orig.total_vat || 0)),
          total_ttc:       -(Math.abs(orig.total_ttc || 0)),
          amount:          -(Math.abs(orig.amount    || 0)),
          deposit_amount:  0,
          status:          'draft',
          issue_date:      new Date().toISOString().split('T')[0],
          payment_method:  orig.payment_method,
          notes:           orig.notes,
          credit_note_for: invoiceId,
        })
        .select('id').single();
      if (error) throw new Error(`Impossible de créer l'avoir : ${error.message}`);

      await (supabase as any)
        .from('invoices')
        .update({ cancelled_by: cn.id, status: 'cancelled' })
        .eq('id', invoiceId);

      return cn.id as string;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  });
}
